import React, { useEffect, useState } from 'react'
import {
  BlockingLoader,
  Container,
  Tabs,
  ToastContext
} from '@pdrj/pmi-iqos-ds-react'
import {
  parseTranslations,
  PublishedComponent,
  CommonTranslations,
  setTranslations,
  constants,
  analytics,
  cookie
} from '@pdrj/pmi-iqos-shared-react'
import { PointsEarningActions } from '@iqosphere-common/components'
import { getInitialPropsMock, buildActionData } from '@iqosphere-common/utils'
import { IqosphereApiManager } from '@iqosphere-common/services'
import ProfilePointsTranslations from './profile-points.json'
import ProfilePointsHistory from './points-history'
import ProfilePointsExpiring from './points-expiring'

const EARNING_SUBSECTION = 'earning'
const HISTORY_SUBSECTION = 'history'
const EXPIRING_SUBSECTION = 'expiring'

const ProfilePointsComponent = ({ translations, qoinActions, line }) => {
  const api = new IqosphereApiManager(line)

  const historyFilters = [
    {
      id: 'all',
      label: translations.tk_filterDefault
    },
    {
      id: 'action',
      label: translations.tk_filterAction
    },
    {
      id: 'reward',
      label: translations.tk_filterReward
    },
    {
      id: 'prizes',
      label: translations.tk_filterPrizes
    }
  ]

  const [expiringData, setExpiringData] = useState([])
  const [expiringDataPaginated, setExpiringDataPaginated] = useState([])
  const [historyData, setHistoryData] = useState([])
  const [historyType, setHistoryType] = useState(historyFilters[0].id)
  const [historyPaginationIndex, setHistoryPaginationIndex] = useState(0)
  const [showLoadMoreHistory, setShowLoadMoreHistory] = useState(0)
  const [selectedTabItemIndex, setSelectedTabItemIndex] = useState(0)
  const [expiringPaginationIndex, setExpiringPaginationIndex] = useState(0)
  const [earningActions, setEarningActions] = useState([])
  const [earnedActions, setEarnedActions] = useState([])
  const [loading, setLoading] = useState(true)
  const [isAV, setIsAV] = useState(false)
  const expiringPageSize = 10
  const historyPageSize = 10

  const tabsItems = [
    ...(isAV ? [translations.tk_tab1] : []),
    translations.tk_tab2,
    translations.tk_tab3
  ]
  if (translations) {
    setTranslations(translations)
  }

  const popupToast = ToastContext.getContextActions().popupToast

  useEffect(() => {
    handleUrlLoaded()
    loadData()
  }, [])

  const handleLoadError = () => {
    popupToast({
      type: ToastContext.types.error,
      title: translations.tk_errorLoading
    })
  }

  const handleUrlLoaded = () => {
    if (window.location.hash) {
      const splits = window.location.hash.split(constants.SUBSECTION_SEPARATOR)
      switch (splits.length > 1 && splits[1]) {
        case EARNING_SUBSECTION:
          setSelectedTabItemIndex(0)
          break
        case HISTORY_SUBSECTION:
          setSelectedTabItemIndex(1)
          break
        case EXPIRING_SUBSECTION:
          setSelectedTabItemIndex(2)
          break
      }
    }
  }

  const loadData = async () => {
    try {
      setLoading(true)
      const tokenInfo = cookie.getTokenInfo()

      if (tokenInfo && tokenInfo.isAV) {
        setIsAV(true)
      } else {
        setSelectedTabItemIndex(1)
      }

      const resActions = await api.iqosphereProfile.getQoinsActions()
      const resExpiring = await api.iqosphereProfile.getQoinsExpiring()
      await getHistory(historyFilters[0].id)
      setExpiringData(resExpiring.expiring)
      setExpiringDataPaginated(resExpiring.expiring.slice(0, expiringPageSize))
      const actionData = buildActionData(qoinActions, resActions, translations)
      setEarningActions(
        actionData.filter(
          action => !action.validated && JSON.parse(action.feature) === false
        )
      )
      setEarnedActions(actionData.filter(action => action.validated))
    } catch (error) {
      handleLoadError()
    } finally {
      setLoading(false)
    }
  }

  const getHistory = async (type, reset = false) => {
    const resHistory = await api.iqosphereProfile.getQoinsHistory(
      reset ? 0 : historyPaginationIndex,
      historyPageSize,
      type
    )
    setHistoryData(
      reset ? resHistory.history : historyData.concat(resHistory.history)
    )
    setHistoryPaginationIndex(reset ? 1 : historyPaginationIndex + 1)
    setShowLoadMoreHistory(resHistory.history.length === historyPageSize)
  }

  const handleClickTab = newSelectedTabItemIndex => {
    if (selectedTabItemIndex !== newSelectedTabItemIndex) {
      setSelectedTabItemIndex(newSelectedTabItemIndex)
    }
  }

  const handleHistoryLoadMore = async () => {
    analytics.clickEvent({
      eventCategory: 'My account',
      eventLabel: 'Points History - Load More'
    })
    try {
      setLoading(true)
      await getHistory(historyType)
    } catch (error) {
      handleLoadError()
    } finally {
      setLoading(false)
    }
  }

  const handleHistoryFilterChange = async type => {
    setHistoryType(type)
    try {
      setLoading(true)
      await getHistory(type, true)
    } catch (error) {
      handleLoadError()
    } finally {
      setLoading(false)
    }
  }

  const handleExpiringLoadMore = async () => {
    analytics.clickProfileQoinsExpiring()
    setLoading(true)
    try {
      const expiringPaginationIndexNew = expiringPaginationIndex + 1
      setExpiringPaginationIndex(expiringPaginationIndexNew)
      const auxPagination = expiringPaginationIndexNew * expiringPageSize
      setExpiringDataPaginated(
        expiringDataPaginated.concat(
          expiringData.slice(auxPagination, auxPagination + expiringPageSize)
        )
      )
    } catch (error) {
      handleLoadError()
    }
    setLoading(false)
  }

  return (
    <Container position={Container.positions.relative}>
      {loading && <BlockingLoader loading />}
      <Tabs
        items={tabsItems.map((tabItem, index) => ({
          tabName: tabItem,
          tabIndex: index + (isAV ? 0 : 1)
        }))}
        isItemSelected={e => e.tabIndex === selectedTabItemIndex}
        getItemLabel={e => e.tabName}
        onItemSelectedHandler={e => {
          handleClickTab(e.tabIndex)
        }}
        data-testing="ProfilePoints_Tabs">
        {selectedTabItemIndex >= 0 && (
          <Container fullWidth bg={Container.backgrounds.hardWhite}>
            {selectedTabItemIndex === 0 && (
              <PointsEarningActions
                translations={translations}
                earningActions={earningActions}
                earnedActions={earnedActions}
                isLoading={loading}
              />
            )}
            {selectedTabItemIndex === 1 && (
              <ProfilePointsHistory
                translations={translations}
                historyData={historyData}
                showLoadMore={showLoadMoreHistory}
                onLoadMoreClickHandler={handleHistoryLoadMore}
                onFilterChangeHandler={type => handleHistoryFilterChange(type)}
                historyType={historyType}
                historyFilters={historyFilters}
                isLoading={loading}
              />
            )}
            {selectedTabItemIndex === 2 && (
              <ProfilePointsExpiring
                translations={translations}
                expiringData={expiringDataPaginated}
                showLoadMore={
                  expiringData &&
                  expiringDataPaginated &&
                  expiringDataPaginated.length < expiringData.length
                }
                onLoadMoreClickHandler={handleExpiringLoadMore}
                isLoading={loading}
              />
            )}
          </Container>
        )}
      </Tabs>
    </Container>
  )
}

const ProfilePoints = props => {
  return (
    <ToastContext>
      <PublishedComponent
        innerComponent={ProfilePointsComponent}
        {...ComponentDefaultProps}
        {...props}
      />
    </ToastContext>
  )
}

const ComponentTranslations = {
  ...ProfilePointsTranslations,
  ...CommonTranslations
}

const ComponentDefaultProps = {
  componentName: 'ProfilePoints',
  translations: {
    ...parseTranslations(ComponentTranslations, true)
  }
}

ProfilePoints.getInitialProps = async req => {
  return PublishedComponent.getGlobalInitialProps({
    context: req && req.query && req.query.context,
    line: !!req?.query?.line,
    componentName: ComponentDefaultProps.componentName,
    defaultTranslations: ComponentDefaultProps.translations,
    translationsConfig: ComponentTranslations,
    getSpecificInitialProps: async () => {
      const qoinActions =
        req && req.query && req.query.mock === 'true'
          ? getInitialPropsMock('profilePointActions')
          : (req.body.extra && req.body.extra.qoinActions) || ''

      return {
        qoinActions
      }
    }
  })
}

export default ProfilePoints
